import {
    AccessTicket,
    accessLeaseExpired,
    accessTicketIsCurrent,
    hasProtectedProject,
} from "./resourceAccess";
import { observeResponseAdmission } from "./responseAdmission";

/** Fetch/SSE use the same admission headers and stale-response fence as Axios.
 * An enforced project stream is checked after each network read, before that chunk
 * is exposed. Unenforced response bodies pass through without a read wrapper.
 */
export function fenceFetchResponse(response: Response, ticket: AccessTicket): Response {
    const check = () => {
        if (!accessTicketIsCurrent(ticket) || accessLeaseExpired()) {
            throw new Error("Project access changed during this request");
        }
    };
    try {
        check();
        observeResponseAdmission(ticket, (name) => response.headers.get(name), response.status);
        check();
    } catch (error) {
        void response.body?.cancel().catch(() => undefined);
        throw error;
    }
    if (!hasProtectedProject() || !response.body) return response;
    const reader = response.body.getReader();
    const body = new ReadableStream<Uint8Array>(
        {
            async pull(controller) {
                try {
                    check();
                    const result = await reader.read();
                    check();
                    if (result.done) controller.close();
                    else controller.enqueue(result.value);
                } catch (error) {
                    await reader.cancel().catch(() => undefined);
                    controller.error(error);
                }
            },
            cancel: (reason) => reader.cancel(reason),
        },
        { highWaterMark: 0 },
    );
    const fenced = new Response(body, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
    });
    Object.defineProperty(fenced, "url", { value: response.url });
    return fenced;
}
